/**
 * Koordinator Auth Page — Passwordless email + Google OAuth
 * No passwords. Single flow for new and returning brands.
 */
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@bright-web-skeleton/ui';
import { SessionExpiredBanner } from '@bright-web-skeleton/ui';
import { FcGoogle } from 'react-icons/fc';
import {
  SESSION_EXPIRED_KEY,
  VerifyEmailScreen,
  ReviewPasswordLogin,
} from '@bright-web-skeleton/auth';
import { useAuth } from '../../shared/contexts';
import { toast } from 'sonner';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const { user, authReady, signInWithGoogle, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectHandledRef = useRef(false);

  const getRedirectPath = (): string => {
    const state = location.state as { from?: string } | null;
    if (state?.from) return state.from;
    try {
      const stored = sessionStorage.getItem('koordinator:authReturnTo');
      if (stored) return stored;
    } catch {
      /* noop */
    }
    return '/command-center';
  };

  // Persist intended destination to sessionStorage so it survives OAuth/magic-link redirects
  useEffect(() => {
    const state = location.state as { from?: string } | null;

    try {
      // A protected-route redirect arriving in this auth journey is fresh.
      if (state?.from) {
        sessionStorage.setItem('koordinator:authReturnTo', state.from);
        return;
      }

      // Brand invite/onboarding flows intentionally stage this destination
      // before entering auth without location.state.
      const stored = sessionStorage.getItem('koordinator:authReturnTo');
      const intentionalOnboardingReturn =
        stored === '/onboarding' || stored?.startsWith('/onboarding/');

      // Any other stored protected destination belongs to an earlier journey
      // and must not override an ordinary Sign In.
      if (stored && !intentionalOnboardingReturn) {
        sessionStorage.removeItem('koordinator:authReturnTo');
      }
    } catch {
      /* noop */
    }
  }, [location.state]);

  // Redirect if already logged in
  useEffect(() => {
    if (!authReady || redirectHandledRef.current || !user) return;
    redirectHandledRef.current = true;
    navigate(getRedirectPath(), { replace: true });
  }, [authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);

    try {
      // Use Supabase OTP directly since koordinator has its own auth context
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: true,
        },
      });
      if (error) {
        toast.error(error.message);
      } else {
        try {
          localStorage.setItem('bright_last_user_email', email);
        } catch {
          /* noop */
        }
        setPendingEmail(email);
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      toast.error(err.message || 'Google sign in failed.');
      setIsLoading(false);
    }
  };

  if (pendingEmail) {
    return (
      <VerifyEmailScreen
        appName="Kloutsale"
        pendingEmail={pendingEmail}
        onBack={() => setPendingEmail(null)}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4"
      data-theme="koordinator"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-primary hover:opacity-80 transition-opacity">
              Kloutsale
            </h1>
          </Link>
          <p className="text-muted-foreground mt-2">Sign in or create your brand account</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">Get Started</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Google */}
            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center justify-center gap-2 mb-6"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <FcGoogle className="w-5 h-5" />
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            {/* Email-only */}
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  disabled={isLoading}
                  required
                />
              </div>

              <Button type="submit" className="w-full min-h-[48px]" disabled={isLoading}>
                <Mail className="mr-2 h-4 w-4" />
                {isLoading ? 'Sending…' : 'Continue with Email'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                We'll send you a secure sign-in link. No password needed.
              </p>
            </form>

            <ReviewPasswordLogin
              email={email}
              onSuccess={() => navigate(getRedirectPath(), { replace: true })}
              onError={(msg) => toast.error(msg)}
            />

            <div className="text-center mt-6">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to Landing
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
